import React, {
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';

import {
  Pane,
  Row,
  Col,
  AccordionStatus,
  AccordionSet,
  ExpandAllButton,
} from '@folio/stripes/components';
import { withStripes } from '@folio/stripes/core';

import {
  LastMenuButton,
  ActionItem,
  NavigateBackPaneFirstMenu,
  RecordLinkageView,
} from '../../common';
import {
  GeneralInformation,
  AssignTasks,
} from './components';
import {
  showRecordLinkageView,
} from '../../../utils';
import {
  DETAIL_VIEW_PANE_WIDTH,
  JOB_FIELDS,
  ICONS,
  JOB_STATUSES,
  RECORD_LINKAGE,
} from '../../../constants';

import cancelJobIcon from './icons';

const JobView = ({
  job,
  onBack,
  onEdit,
  onStartJob,
  onCancelJob,
  onRestartJob,
  stripes,
}) => {
  const hasEditCreatePermission = stripes.hasPerm('ui-task-list.job.create-edit');
  const hasStartJobPermission = stripes.hasPerm('ui-task-list.job.start');
  const hasCancelJobPermission = stripes.hasPerm('ui-task-list.job.cancel');
  const hasRestartJobPermission = stripes.hasPerm('ui-task-list.job.restart');

  const initialAccordionState = {
    general: true,
    metadata: false,
    assignTasks: true,
  };

  const jobStatus = job[JOB_FIELDS.STATUS];
  const createdJob = jobStatus === JOB_STATUSES.CREATED;
  const cancelledJob = jobStatus === JOB_STATUSES.CANCELLED;

  const hasChangeJobStatusPermission = useMemo(() => (
    (createdJob && hasStartJobPermission) ||
      ((!createdJob && !cancelledJob) && hasCancelJobPermission) ||
      (cancelledJob && hasRestartJobPermission)
  ), [createdJob, cancelledJob]);

  const getFirstMenu = useCallback(() => (
    <NavigateBackPaneFirstMenu onClickHandler={onBack} />
  ), [onBack]);

  const getLastMenu = () => {
    if (hasEditCreatePermission && !hasChangeJobStatusPermission) {
      return (
        <LastMenuButton
          id="editJobAction"
          labelId="action.edit"
          onClick={onEdit}
        />
      );
    }

    return null;
  };

  // eslint-disable-next-line react/prop-types
  const getActionMenu = ({ onToggle }) => {
    if (hasChangeJobStatusPermission) {
      return (
        <>
          <ActionItem
            icon={ICONS.EDIT}
            id="editJobAction"
            messageId="action.edit"
            onClickHandler={onEdit}
            onToggle={onToggle}
          />
          {
            createdJob && (
              <ActionItem
                icon={ICONS.ARROW_RIGHT}
                id="startJobAction"
                messageId="action.start.job"
                onClickHandler={onStartJob}
                onToggle={onToggle}
              />
            )
          }
          {
            (!createdJob && !cancelledJob) && (
              <ActionItem
                icon={cancelJobIcon}
                id="cancelJobAction"
                messageId="action.cancel.job"
                onClickHandler={onCancelJob}
                onToggle={onToggle}
              />
            )
          }
          {
            cancelledJob && (
              <ActionItem
                icon={ICONS.ARROW_RIGHT}
                id="restartJobAction"
                messageId="action.restart.job"
                onClickHandler={onRestartJob}
                onToggle={onToggle}
              />
            )
          }
        </>
      );
    }

    return null;
  };

  return (
    <Pane
      data-test-job-details-view
      actionMenu={getActionMenu}
      defaultWidth={DETAIL_VIEW_PANE_WIDTH}
      firstMenu={getFirstMenu()}
      lastMenu={getLastMenu()}
      paneTitle={job[JOB_FIELDS.NAME]}
    >
      <AccordionStatus>
        <Row end="xs">
          <Col xs>
            <ExpandAllButton />
          </Col>
        </Row>
        <AccordionSet initialStatus={initialAccordionState}>
          <GeneralInformation job={job} />
          {showRecordLinkageView(job) &&
            <RecordLinkageView recordLinkage={job[RECORD_LINKAGE]} />
          }
          <AssignTasks assignTask={job[JOB_FIELDS.ASSIGN_TASK]} />
        </AccordionSet>
      </AccordionStatus>
    </Pane>
  );
};

JobView.propTypes = {
  stripes: PropTypes.shape({
    hasPerm: PropTypes.func.isRequired,
  }).isRequired,
  onBack: PropTypes.func.isRequired,
  onCancelJob: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onRestartJob: PropTypes.func.isRequired,
  onStartJob: PropTypes.func.isRequired,
  job: PropTypes.shape({
    recordLinkage: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
    ),
  }),
};

export default withStripes(JobView);
